import * as HeroIconsOutline from '@heroicons/react/outline';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Error } from '../Error';
import { Label } from '../Label';
import { HeroIcons } from '../style/heroicons';
import { applyInputStyles } from '../style/util';
import { appendClassProps } from '../util';
import { InputType } from './index.types';
// Component for conditionally forwarding ref
const UseOverrideRef = ({ forwardedRef, inputRef }) => {
    useImperativeHandle(forwardedRef, () => {
        return {
            ...inputRef?.current,
            focus: () => {
                inputRef?.current?.focus();
            },
            getBoundingClientRect: () => inputRef?.current.getBoundingClientRect(),
            offsetParent: inputRef?.current?.offsetParent
        };
    });
    return null;
};
/**
 - This is a controlled component for text input
 */
const Input = ({ 'data-testid': testId, autoFocus = false, type = InputType.text, revealErrorMessage = false, disabled = false, hideErrorSection = false, loading = false, name, label, tooltip, placeholder, leftIcon, rightIcon, errorMessage, value, className, inputClassName, border, transparent, min, max, step, onChangeValue, onChange, onPressEnter, onBlur, onClickRightIcon }, ref) => {
    const inputRef = useRef(null);
    const [hidden, setHidden] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [rightIconDepressed, setRightIconDepressed] = useState(false);
    if (type === InputType.password) {
        rightIcon = hidden ? HeroIcons.EyeOffIcon : HeroIcons.EyeIcon;
    }
    let LeftIcon;
    let RightIcon;
    if (typeof leftIcon === 'string') {
        LeftIcon = HeroIconsOutline[leftIcon];
    }
    if (typeof rightIcon === 'string') {
        RightIcon = HeroIconsOutline[rightIcon];
    }
    const showErrorMessage = errorMessage && (revealErrorMessage || dirty) ? true : false;
    const handleChange = (event) => {
        const target = event.target;
        if (typeof onChange === 'function') {
            onChange(event);
        }
        else if (typeof onChangeValue === 'function') {
            let newValue = target.value;
            if (type === InputType.number) {
                newValue = step === 'any' ? parseFloat(newValue) : parseInt(newValue);
            }
            onChangeValue(newValue);
        }
    };
    const handleBlur = () => {
        setDirty(true);
        if (onBlur)
            onBlur();
    };
    const handleClickRightIcon = () => {
        if (type === InputType.password) {
            setHidden(!hidden);
        }
        if (onClickRightIcon)
            onClickRightIcon();
    };
    const onRightIconMouseDown = () => {
        if (type === InputType.password) {
            setRightIconDepressed(true);
        }
    };
    const onRightIconMouseUp = () => {
        if (type === InputType.password) {
            setRightIconDepressed(false);
        }
    };
    const onClickLeftIcon = () => {
        inputRef?.current?.focus();
    };
    const onKeyUp = (event) => {
        if (event.key === 'Enter' && typeof onPressEnter === 'function')
            onPressEnter(event);
    };
    return (React.createElement("div", { "data-testid": "div", className: `flex flex-col text-blue-800${appendClassProps(className)}` },
        label && (React.createElement(Label, { dataTestId: "label", tooltip: tooltip }, label)),
        ref && Object.keys(ref).length > 0 ? (React.createElement(UseOverrideRef, { forwardedRef: ref, inputRef: inputRef })) : null,
        React.createElement("div", { className: "relative min-w-36 w-full" },
            leftIcon && (React.createElement("span", { className: `absolute inset-y-0 left-2 flex items-center text-gray-400  ${disabled ? 'cursor-not-allowed' : 'cursor-text'} `, onClick: onClickLeftIcon }, typeof leftIcon === 'string' ? (React.createElement(LeftIcon, { "data-testid": "left-icon", className: "h-5 w-5 lg:h-6 lg:w-6" })) : (leftIcon))),
            rightIcon && (React.createElement("span", { className: `absolute inset-y-0 right-2 flex items-center text-gray-400${type === InputType.password && !disabled ? ' cursor-pointer' : ''}${rightIconDepressed || type !== InputType.password
                    ? ''
                    : ' hover:text-gray-600'}`, onClick: handleClickRightIcon, onMouseDown: onRightIconMouseDown, onMouseUp: onRightIconMouseUp }, typeof rightIcon === 'string' ? (React.createElement(RightIcon, { "data-testid": "right-icon", className: "h-5 w-5 lg:h-6 lg:w-6" })) : (rightIcon))),
            loading ? (React.createElement(Skeleton, { width: '100%', height: '34px' })) : (React.createElement("input", { "data-testid": testId ?? 'input', ref: inputRef, name: name, type: hidden ? InputType.text : type, onChange: handleChange, onBlur: handleBlur, onKeyUp: onKeyUp, placeholder: placeholder, autoFocus: autoFocus, className: applyInputStyles({
                    leftIcon,
                    rightIcon,
                    showErrorMessage,
                    inputClassName,
                    disabled,
                    border,
                    transparent
                }), value: value, disabled: disabled, min: min, max: max, step: step }))),
        !hideErrorSection && (React.createElement(Error, { errorMessage: errorMessage, showErrorMessage: showErrorMessage }))));
};
const WrappedInput = forwardRef(Input);
export * from './index.types';
export { WrappedInput as Input, Input as BareInput }; // for storybook (fails to properly parse component if wrapped with forwardRef)
//# sourceMappingURL=index.js.map